import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ScreenState } from '../lib/types';
import { ApiError } from '../lib/api';
import { validationCopy, validationFieldLabel } from '../lib/labels';

const COPY: Record<ScreenState, { title: string; body: string; action?: string }> = {
  READY: { title: '', body: '' },
  LOADING: { title: '불러오는 중', body: '' },
  EMPTY: { title: '항목 없음', body: '항목 추가 후 목록에서 확인 가능' },
  SEARCH_EMPTY: { title: '검색 결과 없음', body: '다른 검색어·조건으로 검색', action: '필터 초기화' },
  PARTIAL: { title: '일부만 불러옴', body: '일부 데이터 누락 · 표시된 값 확인 필요', action: '다시 시도' },
  STALE: { title: '최신 값 아님', body: '갱신 중단 · 마지막 집계 시각 확인', action: '다시 시도' },
  OFFLINE: { title: '연결 끊김', body: '네트워크 확인 후 다시 시도', action: '다시 시도' },
  FORBIDDEN: { title: '권한 없음', body: '작업 권한 필요' },
  NOT_FOUND: { title: '찾을 수 없음', body: '항목 삭제 또는 접근 제한' },
  EXPIRED: { title: '기한 지남', body: '유효 기간 만료 · 처리 불가' },
  CONFLICT: { title: '다른 변경과 충돌', body: '최신 내용 확인 후 다시 저장', action: '최신 내용 보기' },
  VALIDATION_ERROR: { title: '입력값 확인 필요', body: '표시된 항목 수정 후 다시 저장' },
  SUBMITTING: { title: '처리 중', body: '완료까지 창 유지 필요' },
  SUCCEEDED: { title: '완료', body: '' },
  PENDING: { title: '처리 대기', body: '서버 확인 후 결과 반영', action: '결과 다시 보기' },
  ALREADY_PROCESSED: { title: '이미 처리됨', body: '기존 처리 결과' },
  POLICY_BLOCKED: { title: '정책상 차단', body: '플랫폼 정책·검토 상태 확인 필요' },
  NOT_CONFIGURED: { title: '연동 미설정', body: '연동 설정 필요' },
  NOT_VERIFIED: { title: '연동 미검증', body: '실제 연결 확인 필요' },
  RATE_LIMITED: { title: '요청 과다', body: '잠시 후 다시 시도', action: '다시 시도' },
  SUSPENDED: { title: '이용 제한', body: '이용 제한 해제 필요' },
  UNSUPPORTED: { title: '지원하지 않음', body: '현재 플랫폼·설정 미지원' },
  ERROR_RETRYABLE: { title: '일시적 오류', body: '잠시 후 다시 시도', action: '다시 시도' },
  ERROR_FATAL: { title: '처리 실패', body: '잠시 후 다시 시도 · 계속되면 고객지원 문의' },
};
/**
 * The single renderer for every non-ready screen state.
 *
 * Request identifiers remain in the API response for diagnostics and are not
 * exposed as product copy. Sensitive server failures use a generic message.
 */
export function StateCard({
  state,
  error,
  onRetry,
  children,
  detail,
}: {
  state: ScreenState;
  error?: ApiError | null;
  onRetry?: () => void;
  children?: ReactNode;
  detail?: ReactNode;
}) {
  if (state === 'READY') return <>{children}</>;

  const signInRequired = error?.code === 'UNAUTHENTICATED';
  const signInPath = window.location.pathname.startsWith('/admin') ? '/admin/sign-in' : '/account/sign-in';
  const copy = COPY[state] ?? COPY.ERROR_FATAL;
  const message = signInRequired
    ? '로그인 후 계정 정보 확인'
    : state === 'VALIDATION_ERROR' ? copy.body
      : error?.message && error.status !== 500 ? error.message : copy.body;

  return (
    <div className="state-card" role={state === 'LOADING' ? 'status' : 'alert'} aria-live="polite">
      <div className="state-card__title">{signInRequired ? '로그인 필요' : copy.title}</div>
      <div className="state-card__body">{message}</div>
      {detail}
      {error?.fields && Object.keys(error.fields).length > 0 && (
        <ul className="state-card__body" style={{ marginTop: 12, textAlign: 'left' }}>
          {Object.entries(error.fields).map(([field, value]) => (
            <li key={field}>
              <strong>{validationFieldLabel(field)}</strong>: {validationCopy(value)}
            </li>
          ))}
        </ul>
      )}
      {/* A retry button during LOADING or SUBMITTING would invite a second request
          for something that is already in flight. */}
      {signInRequired && (
        <div className="state-card__actions">
          <Link className="btn btn--primary" to={signInPath}>로그인</Link>
        </div>
      )}
      {onRetry && !signInRequired && state !== 'LOADING' && state !== 'SUBMITTING' && (
        <div className="state-card__actions">
          <button type="button" className="btn" onClick={onRetry}>{copy.action ?? '다시 시도'}</button>
        </div>
      )}
    </div>
  );
}

export function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'danger' | 'info' | 'neutral'; children: ReactNode }) {
  return <span className={`pill pill--${tone}`}>{children}</span>;
}

/** Maps a domain status onto a pill tone. Colour never stands alone — the label is always shown. */
export function statusTone(status: string): 'ok' | 'warn' | 'danger' | 'info' | 'neutral' {
  const value = status.toUpperCase();
  if (['PUBLISHED', 'ACTIVE', 'LIVE', 'GRANTED', 'VERIFIED', 'OK', 'DELIVERED', 'RESOLVED', 'COMPLETED', 'ENABLED', 'SENT'].includes(value)) return 'ok';
  if (['DRAFT', 'SCHEDULED', 'PENDING', 'APPROVAL_PENDING', 'PENDING_VERIFICATION', 'IN_PROGRESS', 'VALIDATED', 'PARTIAL'].includes(value)) return 'info';
  if (['DEGRADED', 'STALE', 'NOT_CONFIGURED', 'NOT_VERIFIED', 'UNREVIEWED', 'PENDING_USER', 'ACK_REQUIRED', 'PARTIALLY_COMPLETED'].includes(value)) return 'warn';
  if (['BLOCKED', 'POLICY_BLOCKED', 'FAILED', 'HALTED', 'REJECTED', 'REVOKED', 'REFUNDED', 'DEAD', 'VOIDED', 'SANCTIONED', 'RETIRED'].includes(value)) return 'danger';
  return 'neutral';
}
